'use client';

/**
 * @packageDocumentation
 * @category Component
 */

import 'BomdisoftComponents/ask-dialog/styles.css';
import { Button } from 'BomdisoftComponents/buttons/index';
import { Dialog } from 'BomdisoftComponents/dialog/index';
import { ArrowDownwardRoundedIcon } from 'BomdisoftComponents/icons/arrow-downward-rounded';
import { TextField } from 'BomdisoftComponents/inputs/field/text';
import { Tooltip } from 'BomdisoftComponents/tooltip/index';
import type { FormEvent, KeyboardEvent, ReactNode } from 'react';

export type AskDialogProps = {
  canAsk?: boolean;
  children: ReactNode;
  className?: string;
  composerActions?: ReactNode;
  formTestId: string;
  hint: string;
  inputTestId: string;
  isAsking: boolean;
  onClose: () => void;
  onKeyDown?: (event: KeyboardEvent<HTMLTextAreaElement>) => void;
  onQuestionChange: (question: string) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  open: boolean;
  question: string;
  sendLabel: string;
  showsComposer?: boolean;
  submitTestId: string;
  testId: string;
};

/**
 * The ask surface both consoles open: a composer card holding the question and
 * its send control, and a panel card beneath it holding whatever the app
 * answers with. The app owns the request, the refusals and the whole answer.
 */
export const AskDialog = ({ canAsk = true, children, className, composerActions, formTestId, hint, inputTestId, isAsking, onClose, onKeyDown, onQuestionChange, onSubmit, open, question, sendLabel, showsComposer = true, submitTestId, testId }: AskDialogProps): ReactNode => (
  <Dialog className={className === undefined ? 'bomdisoft-ask' : `bomdisoft-ask ${className}`} form onClose={onClose} open={open} size='wide' testId={testId}>
    {showsComposer ? (
      <div className='bomdisoft-ask-composer'>
        <form data-testid={formTestId} onSubmit={onSubmit}>
          <TextField
            aria-label={hint}
            className='bomdisoft-ask-question'
            data-testid={inputTestId}
            endAdornment={
              <Tooltip title={sendLabel}>
                <Button aria-label={sendLabel} className='bomdisoft-ask-send' data-testid={submitTestId} disabled={isAsking || question.trim() === '' || !canAsk} type='submit' variant='tint'>
                  <ArrowDownwardRoundedIcon size={16} />
                </Button>
              </Tooltip>
            }
            multiline
            onChange={event => onQuestionChange(event.target.value)}
            onKeyDown={onKeyDown}
            placeholder={hint}
            rows={1}
            value={question}
          />
        </form>
        {composerActions === undefined ? null : <div className='bomdisoft-ask-actions'>{composerActions}</div>}
      </div>
    ) : null}
    <div className='bomdisoft-ask-panel'>{children}</div>
  </Dialog>
);

export type AskAnswerProps = { children: ReactNode; testId: string };

export const AskAnswer = ({ children, testId }: AskAnswerProps): ReactNode => (
  <div className='bomdisoft-ask-answer' data-testid={testId}>
    {children}
  </div>
);

/** The asked question, as the answer's heading. */
export const AskQuestionEcho = ({ children }: { children: ReactNode }): ReactNode => <h2 className='bomdisoft-ask-echo'>{children}</h2>;

export type AskProseProps = { children: ReactNode; testId: string };

export const AskProse = ({ children, testId }: AskProseProps): ReactNode => (
  <p className='bomdisoft-ask-prose' data-testid={testId}>
    {children}
  </p>
);

export type AskThinkingProps = { label: string; testId?: string };

export const AskThinking = ({ label, testId }: AskThinkingProps): ReactNode => (
  <p className='bomdisoft-ask-thinking' data-testid={testId}>
    {label}
  </p>
);
